import { useState, useEffect, useMemo, useCallback } from "react";
import {
    Webhook, Mic, Sheet, Mail, Loader2, PlayCircle, Eye,
    Download, Filter, Shield, Building2, Info, Copy, CheckCircle2, AlertTriangle,
    RefreshCcw, MessageSquare, CreditCard, ExternalLink, Users, Wrench, Phone, PhoneCall, Command, Plus, Minus
} from "lucide-react";
import {
    fetchConnections, upsertConnection, testConnection, syncConnection,
    deactivateConnection, testEmailSend, fetchIntegrationLogs, clearTestLogs,
    fetchDataMappings, upsertDataMapping, previewDataMapping, fetchLatestSync, formatApiError,
    fetchPostCalls,
    fetchElevenLabsSettings, updateElevenLabsPostCall, rotateVoiceIntakeSecret, revealVoiceIntakeSecret, probeElevenLabsConfig,
    fetchBillingSubscription, createBillingPortalSession, fetchBillingPackages, changeBillingPlan,
    fetchBillingAddons, updateBillingAddons, createTopupCheckout, fetchCompanyUsage,
    fetchBillingProfile, updateBillingProfile,
} from "@/lib/api";
import { PageHeader, RangeToggle, useRange } from "@/components/dashboard/Primitives";
import SearchInput from "@/components/dashboard/SearchInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AIPhonePanel from "@/components/dashboard/AIPhonePanel";
import AICompanyDataTab from "@/components/dashboard/AICompanyDataTab";

const TABS = [
    { v: "integrations", l: "Integrationer" },
    { v: "ai-data", l: "AI & Virksomhedsdata" },
    { v: "mapping", l: "Data mapping" },
    { v: "logs", l: "Sync & logs" },
    { v: "billing", l: "Billing" },
    { v: "security", l: "Sikkerhed" },
    { v: "company", l: "Firma" },
];

// ---------- Status badge ----------
function StatusBadge({ status }) {
    const map = {
        active: { l: "Aktiv", c: "bg-emerald-50 text-emerald-700 border-emerald-100" },
        configured: { l: "Konfigureret", c: "bg-blue-50 text-blue-700 border-blue-100" },
        error: { l: "Fejl", c: "bg-red-50 text-red-700 border-red-100" },
        inactive: { l: "Deaktiveret", c: "bg-slate-100 text-slate-600 border-slate-200" },
        not_configured: { l: "Ikke konfigureret", c: "bg-slate-100 text-slate-500 border-slate-200" },
        ok: { l: "OK", c: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    };
    const m = map[status] || map.not_configured;
    return <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border ${m.c}`}>{m.l}</span>;
}

// ---------- Generic integration card ----------
const INTEGRATION_DEFS = [
    {
        type: "google_sheets", icon: Sheet, accent: "from-emerald-500 to-emerald-600",
        title: "Google Sheets Data",
        desc: "Google Sheets bruges som simpelt datalager i V1. Dashboardet viser data pænt, mens Sheets kan bruges som backup.",
        topFields: [
            { k: "sheet_url", label: "Sheet URL", placeholder: "https://docs.google.com/spreadsheets/...", root: true },
            { k: "sheet_id", label: "Sheet ID", placeholder: "1aB...xyZ", root: true, mono: true },
            { k: "api_key", label: "API key / service token", secret: true, root: true },
        ],
        cfgFields: [],
        showCounts: ["rows_fetched", "rows_updated", "last_sync"],
        supportsSync: true,
    },
    {
        type: "email", icon: Mail, accent: "from-orange-500 to-orange-600",
        title: "Email-afsendelse",
        desc: "Email bruges senere til invitationer, fakturaer og automatiske beskeder.",
        cfgFields: [
            { k: "provider", label: "Provider", placeholder: "gmail_smtp / sendgrid / resend / custom_smtp" },
            { k: "from_email", label: "From email", placeholder: "noreply@dit-firma.dk" },
            { k: "from_name", label: "From name", placeholder: "MyServiceHelper" },
            { k: "smtp_host", label: "SMTP host" },
            { k: "smtp_port", label: "SMTP port", placeholder: "587" },
            { k: "smtp_username", label: "SMTP username" },
            { k: "smtp_password", label: "SMTP password / API key", secret: true },
        ],
        showCounts: ["last_sent", "last_error"],
        supportsTestEmail: true,
    },
];

function getRoot(conn, k) { return conn?.[k] ?? ""; }
function getCfg(conn, k) { return (conn?.config || {})[k] ?? ""; }
function isSecretSet(conn, k, root) {
    if (root) return !!conn?.api_key_set;
    return !!(conn?.config || {})[`${k}_set`];
}

function IntegrationCard({ def, conn, onSaved, onLog, disabled }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [copiedKey, setCopiedKey] = useState("");
    const Icon = def.icon;

    useEffect(() => {
        const next = {};
        (def.topFields || []).forEach((f) => {
            // For read-only secrets (inbound webhooks), show actual value from connection
            if (f.readOnly) next[f.k] = getRoot(conn, f.k);
            else next[f.k] = f.secret ? "" : getRoot(conn, f.k);
        });
        (def.cfgFields || []).forEach((f) => { next[f.k] = f.secret ? "" : getCfg(conn, f.k); });
        setForm(next);
    }, [conn, def]);

    const status = !conn ? "not_configured" : (!conn.active ? "inactive" : (conn.status || "configured"));
    const lastTested = conn?.last_tested_at ? new Date(conn.last_tested_at).toLocaleString("da-DK") : "—";
    const lastSync = conn?.last_sync_at ? new Date(conn.last_sync_at).toLocaleString("da-DK") : "—";

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = { type: def.type, config: {}, active: conn?.active ?? true };
            (def.topFields || []).forEach((f) => {
                if (form[f.k] !== "" && form[f.k] !== undefined) payload[f.k] = form[f.k];
                else if (f.secret && isSecretSet(conn, f.k, true) && (form[f.k] === "" || form[f.k] === undefined)) {
                    /* keep existing */
                }
            });
            (def.cfgFields || []).forEach((f) => {
                if (form[f.k] !== undefined) payload.config[f.k] = form[f.k];
            });
            const saved = await upsertConnection(payload);
            onSaved(saved);
            toast.success(`${def.title} gemt`);
            onLog && onLog();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const r = await testConnection(def.type);
            if (r.ok) toast.success(r.message); else toast.error(r.message);
            onSaved({ ...(conn || {}), status: r.ok ? "active" : "error", last_tested_at: new Date().toISOString(), last_error: r.ok ? "" : r.message });
            onLog && onLog();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setTesting(false); }
    };

    const handleSync = async () => {
        setSyncing(true);
        try { const r = await syncConnection(def.type); toast.success(r.message); onSaved({ ...(conn || {}), last_sync_at: r.last_sync_at, status: "active" }); onLog && onLog(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setSyncing(false); }
    };

    const handleDeactivate = async () => {
        try { await deactivateConnection(def.type); onSaved({ ...(conn || {}), active: false, status: "configured" }); toast.success("Deaktiveret"); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const handleCopy = async (val, k) => {
        try { await navigator.clipboard.writeText(val || ""); setCopiedKey(k); setTimeout(() => setCopiedKey(""), 1500); }
        catch { toast.error("Kunne ikke kopiere"); }
    };

    const renderField = (f) => {
        const isReadOnly = !!f.readOnly;
        const isCopyable = !!f.copyable;
        const value = form[f.k] ?? "";
        return (
            <div key={f.k} className={f.k === "smtp_password" || f.k === "from_email" || f.k === "sheet_url" ? "sm:col-span-2" : ""}>
                <Label className="text-xs text-slate-600">
                    {f.label}
                    {f.secret && !isReadOnly && isSecretSet(conn, f.k, f.root) ? <span className="ml-1 text-emerald-600 text-[10px]">(sat)</span> : null}
                    {isReadOnly ? <span className="ml-1 text-slate-400 text-[10px]">(auto)</span> : null}
                </Label>
                <div className={isCopyable ? "flex items-center gap-1.5 mt-1" : ""}>
                    <Input
                        data-testid={`${def.type}-field-${f.k}`}
                        type={f.secret && !isReadOnly ? "password" : "text"}
                        disabled={disabled || isReadOnly}
                        readOnly={isReadOnly}
                        value={value}
                        onChange={(e) => setForm((p) => ({ ...p, [f.k]: e.target.value }))}
                        placeholder={f.secret && !isReadOnly && isSecretSet(conn, f.k, f.root) ? "••••••••••• (lad stå tom for at beholde)" : (f.placeholder || "")}
                        className={`h-10 ${isCopyable ? "" : "mt-1"} text-sm ${f.mono || isReadOnly ? "font-mono text-xs" : ""} ${isReadOnly ? "bg-slate-50 text-slate-700" : ""}`}
                    />
                    {isCopyable && (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleCopy(value, f.k)}
                            data-testid={`${def.type}-field-${f.k}-copy`}
                            className="h-10 px-2 shrink-0"
                        >
                            {copiedKey === f.k ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                    )}
                    {f.regenerate && (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled
                            data-testid={`${def.type}-field-${f.k}-regen`}
                            className="h-10 px-2 shrink-0"
                            title="Deprecated"
                        >
                            <RefreshCcw className="w-3.5 h-3.5" />
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div data-testid={`integration-${def.type}`} className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <span className={`w-11 h-11 rounded-xl bg-gradient-to-br ${def.accent} text-white inline-flex items-center justify-center shadow`}>
                        <Icon className="w-5 h-5" />
                    </span>
                    <div>
                        <h3 className="text-base font-bold text-slate-900">{def.title}</h3>
                        <p className="text-xs text-slate-500 mt-0.5 max-w-md">{def.desc}</p>
                    </div>
                </div>
                <StatusBadge status={status} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(def.topFields || []).map(renderField)}
                {(def.cfgFields || []).map(renderField)}
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500 bg-slate-50/70 rounded-xl px-3 py-2">
                <span>Sidst testet: <span className="text-slate-700 font-medium">{lastTested}</span></span>
                <span>Sidste sync/event: <span className="text-slate-700 font-medium">{lastSync}</span></span>
                {conn?.last_error && <span className="col-span-2 text-red-600">Sidste fejl: {conn.last_error}</span>}
            </div>

            <div className="flex flex-wrap gap-2">
                <Button onClick={handleSave} disabled={disabled || saving} data-testid={`${def.type}-save`} className="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Gem
                </Button>
                <Button onClick={handleTest} disabled={disabled || testing} variant="outline" data-testid={`${def.type}-test`}>
                    {testing && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Test forbindelse
                </Button>
                {def.supportsSync && (
                    <Button onClick={handleSync} disabled={disabled || syncing} variant="outline" data-testid={`${def.type}-sync`}>
                        {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlayCircle className="w-4 h-4 mr-1.5" />} Sync nu
                    </Button>
                )}
                {def.supportsTestEmail && (
                    <SendTestEmailButton disabled={disabled} onLog={onLog} />
                )}
                {conn?.active && (
                    <Button onClick={handleDeactivate} disabled={disabled} variant="outline" data-testid={`${def.type}-deactivate`} className="text-red-600 hover:bg-red-50 hover:text-red-700">
                        Deaktivér
                    </Button>
                )}
            </div>
        </div>
    );
}

// =========================================================================
// ElevenLabs Integration Section (Sprint C3)
// Two custom cards: Post-Call Webhook + Voice Intake
// =========================================================================

function StatusPill({ ok, label }) {
    return (
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${
            ok ? "bg-emerald-50 text-emerald-700 border-emerald-100"
               : "bg-slate-100 text-slate-500 border-slate-200"
        }`}>
            {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {label}
        </span>
    );
}

function CopyRow({ value, testid, mono = true }) {
    const [copied, setCopied] = useState(false);
    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(value || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { toast.error("Kunne ikke kopiere"); }
    };
    return (
        <div className="flex items-center gap-1.5">
            <Input readOnly value={value || ""} data-testid={testid}
                className={`h-9 text-xs ${mono ? "font-mono" : ""} bg-slate-50 text-slate-700`} />
            <Button type="button" size="sm" variant="outline" onClick={doCopy}
                data-testid={`${testid}-copy`} className="h-9 px-2 shrink-0">
                {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
        </div>
    );
}

function fmtRelative(iso) {
    if (!iso) return "Aldrig";
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffSec = Math.round((now - d) / 1000);
        if (diffSec < 60) return `${diffSec}s siden`;
        if (diffSec < 3600) return `${Math.round(diffSec/60)} min siden`;
        if (diffSec < 86400) return `${Math.round(diffSec/3600)} timer siden`;
        return d.toLocaleString("da-DK");
    } catch { return "—"; }
}

function ElevenLabsIntegrationSection({ canEdit, refreshLogs }) {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { setSettings(await fetchElevenLabsSettings()); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Indlæser ElevenLabs-integration…
            </div>
        );
    }

    return (
        <section className="space-y-3" data-testid="elevenlabs-integration-section">
            <header className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 text-white inline-flex items-center justify-center shadow">
                    <Mic className="w-4 h-4" />
                </span>
                <div>
                    <h3 className="text-base font-bold text-slate-900">ElevenLabs</h3>
                    <p className="text-xs text-slate-500">Voice intake + post-call transcript — direkte fra ElevenLabs.</p>
                </div>
                {settings?.native_enabled ? (
                    <StatusPill ok={true} label="Aktiveret" />
                ) : (
                    <StatusPill ok={false} label="Deaktiveret pr. virksomhed" />
                )}
                {settings?.errors_24h > 0 && (
                    <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-100" data-testid="elevenlabs-errors-24h">
                        {settings.errors_24h} fejl (24t)
                    </span>
                )}
            </header>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <PostCallWebhookCard data={settings?.post_call} onSaved={load} canEdit={canEdit} refreshLogs={refreshLogs} />
                <VoiceIntakeCard data={settings?.voice_intake} onSaved={load} canEdit={canEdit} refreshLogs={refreshLogs} />
            </div>

            {settings?.last_error && (
                <div className="text-[11px] bg-red-50 border border-red-100 rounded-lg p-2.5 text-red-800" data-testid="elevenlabs-last-error">
                    <strong>Seneste fejl</strong> ({fmtRelative(settings.last_error_at)}): {settings.last_error}
                </div>
            )}
        </section>
    );
}

function PostCallWebhookCard({ data, onSaved, canEdit }) {
    const [agentId, setAgentId] = useState(data?.agent_id || "");
    const [secret, setSecret] = useState("");
    const [saving, setSaving] = useState(false);
    const [probing, setProbing] = useState(false);
    const [probeResult, setProbeResult] = useState(null);

    useEffect(() => { setAgentId(data?.agent_id || ""); }, [data?.agent_id]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { agent_id: agentId };
            if (secret.trim()) payload.post_call_secret = secret.trim();
            await updateElevenLabsPostCall(payload);
            setSecret("");
            toast.success("Post-Call konfiguration gemt");
            onSaved();
        } catch (e) {
            const code = e?.response?.data?.detail;
            if (code === "elevenlabs_agent_already_assigned") {
                toast.error("Denne ElevenLabs-agent er allerede forbundet til en anden kundevirksomhed.");
            } else {
                toast.error(formatApiError(e));
            }
        }
        finally { setSaving(false); }
    };

    const probe = async () => {
        setProbing(true); setProbeResult(null);
        try {
            const r = await probeElevenLabsConfig();
            setProbeResult(r);
            if (r.ok) toast.success("Konfiguration er OK");
            else toast.error("Konfiguration har fejl — se detaljer");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setProbing(false); }
    };

    return (
        <article className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="elevenlabs-post-call-card">
            <header className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white inline-flex items-center justify-center shadow">
                        <Webhook className="w-4.5 h-4.5" />
                    </span>
                    <div>
                        <h4 className="text-sm font-bold text-slate-900">Post-Call Webhook</h4>
                        <p className="text-[11px] text-slate-500 max-w-sm">Transcript og AI-analyse efter afsluttet opkald. Signeret med HMAC-SHA256.</p>
                    </div>
                </div>
                <StatusPill ok={data?.secret_configured && !!data?.agent_id} label={data?.secret_configured && data?.agent_id ? "Konfigureret" : "Ikke konfigureret"} />
            </header>

            <div className="space-y-3">
                <div>
                    <Label className="text-[11px] text-slate-600">Agent ID</Label>
                    <Input
                        data-testid="post-call-agent-id"
                        value={agentId}
                        onChange={(e) => setAgentId(e.target.value)}
                        placeholder="agent_..."
                        disabled={!canEdit}
                        className="h-9 mt-1 text-sm font-mono"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">ElevenLabs → Conversational AI → din agent → kopier ID øverst.</p>
                </div>

                <div>
                    <Label className="text-[11px] text-slate-600">ElevenLabs Post-Call URL <span className="text-slate-400">(auto-genereret)</span></Label>
                    <CopyRow value={data?.post_call_url} testid="post-call-url" />
                    <p className="text-[10px] text-slate-400 mt-1">Indsæt dette link i ElevenLabs under <strong>Settings → Post-Call Webhooks</strong>.</p>
                </div>

                <div>
                    <Label className="text-[11px] text-slate-600">
                        ElevenLabs Webhook Secret
                        {data?.secret_configured && <span className="ml-1 text-emerald-600 text-[10px]">(gemt)</span>}
                    </Label>
                    <Input
                        data-testid="post-call-secret"
                        type="password"
                        value={secret}
                        onChange={(e) => setSecret(e.target.value)}
                        placeholder={data?.secret_configured ? "••••••••••• (lad stå tom for at beholde)" : "Indsæt secret fra ElevenLabs"}
                        disabled={!canEdit}
                        className="h-9 mt-1 text-sm font-mono"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Kopiér <strong>Webhook Secret</strong> fra ElevenLabs når du opretter Post-Call Webhook. Vises aldrig igen efter lagring.</p>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                <span>Sidst modtaget: <span className="text-slate-800 font-medium">{fmtRelative(data?.last_event_at)}</span></span>
                <span>Status: <span className="text-slate-800 font-medium">{data?.last_status || "—"}</span></span>
            </div>

            {probeResult && (
                <div className="space-y-1 text-[11px] bg-slate-50 rounded-lg p-2.5 border border-slate-200" data-testid="post-call-probe-result">
                    {probeResult.checks.map((c) => (
                        <div key={c.name} className="flex items-start gap-1.5">
                            {c.ok ? <CheckCircle2 className="w-3 h-3 text-emerald-600 mt-0.5" /> : <AlertTriangle className="w-3 h-3 text-red-600 mt-0.5" />}
                            <span className={c.ok ? "text-slate-700" : "text-red-700"}>{c.message}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={save} disabled={!canEdit || saving} data-testid="post-call-save"
                    className="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Gem
                </Button>
                <Button onClick={probe} disabled={probing} variant="outline" data-testid="post-call-probe">
                    {probing && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Kontrollér konfiguration
                </Button>
            </div>
        </article>
    );
}

function VoiceIntakeCard({ data, onSaved, canEdit }) {
    const { user } = useAuth();
    const canReveal = user && (user.role === "owner" || user.role === "admin" || user.is_super_admin);
    const [rotating, setRotating] = useState(false);
    const [revealing, setRevealing] = useState(false);
    const [plaintext, setPlaintext] = useState("");   // shown ONCE right after rotate or reveal
    const [showPlaintext, setShowPlaintext] = useState(false);
    const [plaintextSource, setPlaintextSource] = useState(""); // "rotate" | "reveal"
    const [copiedPT, setCopiedPT] = useState(false);
    const isConfigured = !!(data?.public_integration_id && data?.secret_configured);

    const rotate = async (isFirst) => {
        const msg = isFirst
            ? "Opret Voice Intake secret og webhook URL nu?"
            : "Regenerér Voice Intake secret? Den gamle vil straks blive afvist.";
        if (!window.confirm(msg)) return;
        setRotating(true);
        try {
            const r = await rotateVoiceIntakeSecret();
            setPlaintext(r.voice_intake_secret_plaintext);
            setPlaintextSource("rotate");
            setShowPlaintext(true);
            toast.success(isFirst ? "Voice Intake initialiseret" : "Secret regenereret");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRotating(false); }
    };

    const reveal = async () => {
        setRevealing(true);
        try {
            const r = await revealVoiceIntakeSecret();
            setPlaintext(r.voice_intake_secret_plaintext);
            setPlaintextSource("reveal");
            setShowPlaintext(true);
            toast.success("Secret vist — luk vinduet efter kopiering");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRevealing(false); }
    };

    const copyPT = async () => {
        try {
            await navigator.clipboard.writeText(plaintext || "");
            setCopiedPT(true);
            setTimeout(() => setCopiedPT(false), 1500);
        } catch { toast.error("Kunne ikke kopiere"); }
    };

    return (
        <article className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="elevenlabs-voice-intake-card">
            <header className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white inline-flex items-center justify-center shadow">
                        <Phone className="w-4.5 h-4.5" />
                    </span>
                    <div>
                        <h4 className="text-sm font-bold text-slate-900">Voice Intake</h4>
                        <p className="text-[11px] text-slate-500 max-w-sm">Mid-call custom tool. Gemmer kunde-bekræftede opgavefelter direkte når agenten kalder <code className="text-[10px]">voice_intake</code>.</p>
                    </div>
                </div>
                <StatusPill ok={isConfigured} label={isConfigured ? "Konfigureret" : "Ikke konfigureret"} />
            </header>

            {!isConfigured ? (
                <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-slate-600 mb-3">Voice Intake er ikke initialiseret endnu.</p>
                    <Button onClick={() => rotate(true)} disabled={!canEdit || rotating} data-testid="voice-intake-initialize"
                        className="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                        {rotating && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Initialisér Voice Intake
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    <div>
                        <Label className="text-[11px] text-slate-600">Voice Intake Webhook URL <span className="text-slate-400">(stabil pr. virksomhed)</span></Label>
                        <CopyRow value={data?.voice_intake_url} testid="voice-intake-url" />
                        <p className="text-[10px] text-slate-400 mt-1">Indsæt dette link i ElevenLabs-agenten under <strong>Tools → Add Tool → Webhook</strong>.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[11px] text-slate-600">HTTP-metode</Label>
                            <Input readOnly value="POST" className="h-9 mt-1 text-xs font-mono bg-slate-50" data-testid="voice-intake-method" />
                        </div>
                        <div>
                            <Label className="text-[11px] text-slate-600">Authentication</Label>
                            <Input readOnly value="Authorization: Bearer <secret>" className="h-9 mt-1 text-xs font-mono bg-slate-50" data-testid="voice-intake-auth-hint" />
                        </div>
                    </div>

                    {showPlaintext && plaintext ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 space-y-2" data-testid="voice-intake-plaintext-warning">
                            <div className="flex items-start gap-2 text-[11px] text-amber-900">
                                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                <p>
                                    <strong>{plaintextSource === "reveal" ? "Secret vist" : "Kopier secret nu"}</strong> —{" "}
                                    {plaintextSource === "reveal"
                                        ? "luk vinduet efter du har brugt værdien. Handlingen er audit-logget."
                                        : "vises kun én gang. Efter du lukker denne besked, kan den ikke hentes igen (regenerér for at få en ny)."}
                                </p>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Input readOnly value={plaintext} data-testid="voice-intake-plaintext-value" className="h-9 text-xs font-mono bg-white" />
                                <Button type="button" size="sm" variant="outline" onClick={copyPT} data-testid="voice-intake-plaintext-copy" className="h-9 px-2 shrink-0">
                                    {copiedPT ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                </Button>
                            </div>
                            <Button type="button" size="sm" variant="ghost" onClick={() => { setShowPlaintext(false); setPlaintext(""); }}
                                data-testid="voice-intake-plaintext-dismiss" className="text-[11px] text-amber-800 hover:text-amber-900">
                                Jeg har gemt den — skjul
                            </Button>
                        </div>
                    ) : (
                        <div>
                            <Label className="text-[11px] text-slate-600">
                                Bearer secret
                                <span className="ml-1 text-emerald-600 text-[10px]">(gemt — kan ikke vises)</span>
                            </Label>
                            <div className="flex items-center gap-1.5 mt-1">
                                <Input readOnly value="•••••••••••••••••••••••••" className="h-9 text-xs font-mono bg-slate-50" data-testid="voice-intake-secret-masked" />
                                {canReveal && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={reveal}
                                        disabled={revealing}
                                        data-testid="voice-intake-reveal"
                                        className="h-9 px-3 shrink-0"
                                        title="Vis secret — kræver owner/admin adgang"
                                    >
                                        {revealing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
                                        Vis
                                    </Button>
                                )}
                            </div>
                            {canReveal && (
                                <p className="text-[10px] text-slate-400 mt-1">Kun owner og admin kan afsløre secret&apos;en. Hver visning gemmes i audit-loggen.</p>
                            )}
                        </div>
                    )}

                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-600 space-y-1 leading-relaxed">
                        <p className="font-semibold text-slate-800">ElevenLabs Tool-opsætning:</p>
                        <p><strong>Tool type:</strong> Webhook</p>
                        <p><strong>Name:</strong> <code>voice_intake</code></p>
                        <p><strong>Method:</strong> POST</p>
                        <p><strong>URL:</strong> [Voice Intake Webhook URL ovenfor]</p>
                        <p><strong>Authentication:</strong> Bearer Token (indsæt secretet ovenfor)</p>
                        <p className="pt-1"><strong>Tool description:</strong></p>
                        <p className="italic pl-2">Gemmer den færdige og bekræftede kundehenvendelse i MyServiceHelper. Tool&apos;et må først kaldes, når agenten har indsamlet og bekræftet de nødvendige kunde- og opgaveoplysninger.</p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                <span>Sidst modtaget: <span className="text-slate-800 font-medium">{fmtRelative(data?.last_event_at)}</span></span>
                <span>Status: <span className="text-slate-800 font-medium">{data?.last_status || "—"}</span></span>
            </div>

            {isConfigured && (
                <div className="flex flex-wrap gap-2 pt-1">
                    <Button onClick={() => rotate(false)} disabled={!canEdit || rotating} variant="outline"
                        data-testid="voice-intake-regenerate" className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200">
                        {rotating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCcw className="w-4 h-4 mr-1.5" />}
                        Regenerér secret
                    </Button>
                </div>
            )}
        </article>
    );
}

function SendTestEmailButton({ disabled, onLog }) {
    const [open, setOpen] = useState(false);
    const [to, setTo] = useState("");
    const [sending, setSending] = useState(false);
    const send = async () => {
        setSending(true);
        try { const r = await testEmailSend({ to }); toast.success(r.message); setOpen(false); onLog && onLog(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setSending(false); }
    };
    return (
        <>
            <Button onClick={() => setOpen(true)} variant="outline" data-testid="email-send-test-btn" disabled={disabled}><Mail className="w-4 h-4 mr-1.5" />Send test-email</Button>
            <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Send test-email</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-2">
                        <div>
                            <Label className="text-xs text-slate-600">Modtager</Label>
                            <Input type="email" data-testid="email-send-test-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="dig@dit-firma.dk" className="h-11 mt-1" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setOpen(false)}>Annullér</Button>
                            <Button onClick={send} disabled={sending} data-testid="email-send-test-submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">{sending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Send</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

// ---------- Tab 1: Integrationer ----------
function IntegrationsUsagePanel() {
    const [u, setU] = useState(null);
    useEffect(() => { fetchCompanyUsage().then(setU).catch(() => {}); }, []);
    if (!u) return null;
    const fmt = (m) => (Number(m) || 0).toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const usedMin = (u.used_seconds || 0) / 60;
    const limitMin = (u.monthly_limit_seconds || 0) / 60;
    const remMin = (u.monthly_remaining_seconds || 0) / 60;
    const topupMin = (u.topup_remaining_seconds || 0) / 60;
    const fmtDate = (s) => s ? new Date(s).toLocaleDateString("da-DK", { day: "2-digit", month: "short", year: "numeric" }) : "—";
    const pct = limitMin > 0 ? Math.min(100, Math.round((usedMin / limitMin) * 100)) : 0;
    const barColor = pct >= 100 ? "bg-rose-400" : pct >= 80 ? "bg-amber-400" : "bg-blue-500";
    return (
        <div data-testid="integrations-usage-panel" className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
                <PhoneCall className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-slate-900">AI-opkald — forbrug</h4>
            </div>
            <p className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="integrations-usage-count">
                {fmt(usedMin)} / {Math.round(limitMin)} min.
            </p>
            <div className="mt-2 h-2 w-full max-w-md rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-1.5 text-sm text-slate-500" data-testid="integrations-usage-remaining">{fmt(remMin)} min. tilbage i denne periode</p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><p className="text-xs text-slate-400">Billing-periode</p><p className="font-medium text-slate-800">{fmtDate(u.billing_period_start)} – {fmtDate(u.billing_period_end)}</p></div>
                <div><p className="text-xs text-slate-400">Inkluderede minutter</p><p className="font-medium text-slate-800">{u.base_minutes || 0} min.</p></div>
                <div><p className="text-xs text-slate-400">Månedlige tilvalg</p><p className="font-medium text-slate-800">{u.recurring_extra_minutes || 0} min.</p></div>
                <div><p className="text-xs text-slate-400">Engangssaldo</p><p className="font-medium text-slate-800">{fmt(topupMin)} min.</p></div>
            </div>
        </div>
    );
}

function IntegrationsTab({ conns, reload, canEdit, refreshLogs }) {
    return (
        <div className="space-y-4">
            <IntegrationsUsagePanel />
            <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-4 flex items-start gap-2.5 text-sm text-blue-900">
                <Info className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
                <div>
                    <p className="font-semibold mb-1">Sådan hænger det sammen</p>
                    <p>ElevenLabs modtager kundeopkald og sender data direkte til MyServiceHelper — både <strong>Voice Intake</strong> (mid-samtale kunde-bekræftede felter) og <strong>Post-Call</strong> (transcript + AI-analyse). Alt gemmes direkte i MyServiceHelpers native database.</p>
                </div>
            </div>

            <ElevenLabsIntegrationSection canEdit={canEdit} refreshLogs={refreshLogs} />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {INTEGRATION_DEFS.map((def) => {
                    const c = conns[def.type];
                    return <IntegrationCard key={def.type} def={def} conn={c} onSaved={(s) => reload(s, def.type)} onLog={refreshLogs} disabled={!canEdit} />;
                })}
            </div>
        </div>
    );
}

// ---------- Tab 2: Data Mapping ----------
const MAPPING_DEFS = [
    { entity: "jobs", title: "Jobs", fields: ["job_id", "customer_id", "customer_name", "task_description", "address", "priority", "status", "assigned_employee", "created_at", "deadline"] },
    { entity: "customers", title: "Customers", fields: ["customer_id", "name", "phone", "email", "address", "latest_job", "revenue", "notes"] },
    { entity: "employees", title: "Employees", fields: ["employee_id", "name", "role", "status", "current_job", "current_location", "hours_today", "hours_week", "hours_month", "hours_year"] },
    { entity: "materials", title: "Materials", fields: ["material_id", "name", "stock_quantity", "minimum_quantity", "shelf_location", "purchase_price", "sales_price", "supplier", "product_link"] },
    { entity: "purchase_requests", title: "Indkøbsliste / Purchase requests", fields: ["purchase_id", "material_id", "material_name", "supplier", "product_link", "suggested_quantity", "estimated_total", "status"] },
    { entity: "invoice_ready", title: "Faktura-klar", fields: ["invoice_id", "job_id", "customer", "task", "time", "materials", "employees", "total", "status"] },
    { entity: "logs", title: "Logs", fields: ["timestamp", "source", "event_type", "status", "message", "related_job_id", "error"] },
];

function MappingSection({ def, mapping, onChange, canEdit, onPreview }) {
    const [open, setOpen] = useState(false);
    return (
        <div data-testid={`mapping-${def.entity}`} className="bg-white rounded-2xl border border-slate-200/70 shadow-sm">
            <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between p-4">
                <span className="text-sm font-semibold text-slate-900">{def.title}</span>
                <span className="text-xs text-slate-500">{open ? "Skjul" : "Vis felter"}</span>
            </button>
            {open && (
                <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs text-slate-600">Sheet navn</Label>
                            <Input data-testid={`mapping-${def.entity}-sheet`} disabled={!canEdit} value={mapping?.sheet_name || ""} onChange={(e) => onChange({ ...mapping, entity: def.entity, sheet_name: e.target.value })} placeholder={def.title} className="h-10 mt-1" />
                        </div>
                        <div>
                            <Label className="text-xs text-slate-600">n8n event type</Label>
                            <Input data-testid={`mapping-${def.entity}-event`} disabled={!canEdit} value={mapping?.n8n_event_type || ""} onChange={(e) => onChange({ ...mapping, entity: def.entity, n8n_event_type: e.target.value })} placeholder={`fx ${def.entity}_created`} className="h-10 mt-1" />
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Påkrævede felter — mapping (kanonisk → kilde-feltnavn)</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {def.fields.map((f) => (
                                <div key={f}>
                                    <Label className="text-[11px] text-slate-500">{f}</Label>
                                    <Input
                                        data-testid={`mapping-${def.entity}-field-${f}`}
                                        disabled={!canEdit}
                                        value={(mapping?.fields || {})[f] || ""}
                                        onChange={(e) => onChange({ ...mapping, entity: def.entity, fields: { ...(mapping?.fields || {}), [f]: e.target.value } })}
                                        placeholder={f}
                                        className="h-9 mt-0.5 text-sm"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                        <Button onClick={() => onChange({ ...mapping, entity: def.entity, _save: true })} disabled={!canEdit} data-testid={`mapping-${def.entity}-save`} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">Gem mapping</Button>
                        <Button onClick={() => onPreview(def.entity)} variant="outline" data-testid={`mapping-${def.entity}-preview`}><Eye className="w-3.5 h-3.5 mr-1.5" />Preview data</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function MappingTab({ canEdit }) {
    const [mappings, setMappings] = useState({});
    const [preview, setPreview] = useState(null);
    const [previewMeta, setPreviewMeta] = useState({ entity: "", source: "", message: "" });

    const load = useCallback(async () => {
        try {
            const list = await fetchDataMappings();
            const map = {};
            list.forEach((m) => { map[m.entity] = m; });
            setMappings(map);
        } catch {}
    }, []);

    useEffect(() => { load(); }, [load]);

    const onChange = async (payload) => {
        const { _save, ...rest } = payload;
        setMappings((p) => ({ ...p, [rest.entity]: rest }));
        if (_save) {
            try { await upsertDataMapping(rest); toast.success("Mapping gemt"); }
            catch (e) { toast.error(formatApiError(e)); }
        }
    };

    const onPreview = async (entity) => {
        try {
            const d = await previewDataMapping(entity);
            setPreview(d.rows || []);
            setPreviewMeta({ entity, source: d.source || "none", message: d.message || "" });
        }
        catch (e) { toast.error(formatApiError(e)); setPreview([]); setPreviewMeta({ entity, source: "error", message: "" }); }
    };

    const sourceBadge = (() => {
        const s = previewMeta.source;
        if (s === "n8n") return { l: "LIVE DATA", c: "bg-emerald-50 text-emerald-700 border-emerald-100" };
        if (s === "db" || s === "derived") return { l: "DB / DERIVED DATA", c: "bg-blue-50 text-blue-700 border-blue-100" };
        if (s === "n8n_empty" || s === "n8n_missing") return { l: "MANGLENDE DATA", c: "bg-amber-50 text-amber-700 border-amber-100" };
        return { l: "INGEN DATA", c: "bg-slate-100 text-slate-500 border-slate-200" };
    })();

    return (
        <div className="space-y-3">
            <p className="text-sm text-slate-600">Vælg hvilke ark, workflows eller felter der hører til hver del af dashboardet.</p>
            {MAPPING_DEFS.map((def) => (
                <MappingSection key={def.entity} def={def} mapping={mappings[def.entity]} onChange={onChange} canEdit={canEdit} onPreview={onPreview} />
            ))}
            <Dialog open={preview !== null} onOpenChange={(o) => !o && setPreview(null)}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            Preview — {previewMeta.entity}
                            <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${sourceBadge.c}`}>{sourceBadge.l}</span>
                        </DialogTitle>
                    </DialogHeader>
                    {previewMeta.entity === "jobs" && previewMeta.source !== "n8n" && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-900 mt-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                            <span>Jobs preview bruger fallback data. {previewMeta.message || "Ingen live data tilgængelig for denne kilde."}</span>
                        </div>
                    )}
                    {(preview || []).length === 0 ? (
                        <p className="text-sm text-slate-500 italic mt-3">{previewMeta.message || "Ingen data fundet for denne mapping."}</p>
                    ) : (
                        <pre className="text-xs bg-slate-900 text-slate-100 rounded-xl p-3 mt-2 overflow-x-auto max-h-96 font-mono">{JSON.stringify(preview, null, 2)}</pre>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ---------- Seneste integration-response (legacy backend view) ----------
function downloadJson(data, filename) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function LatestSyncSection({ latest, reload }) {
    const [copied, setCopied] = useState(false);
    const payload = latest?.latest_sync_payload;
    const counts = latest?.latest_sync_counts || {};
    const missing = latest?.latest_sync_missing || [];
    const rawResponse = latest?.latest_raw_response || "";
    const status = latest?.latest_sync_status || "none";
    const data = (payload && typeof payload === "object" && payload.data && typeof payload.data === "object") ? payload.data : null;
    const jobs = Array.isArray(data?.jobs) ? data.jobs : null;

    const isRealData = !!(payload && data && Array.isArray(data?.jobs) && data.jobs.length > 0);

    const copyJson = async () => {
        if (!payload) return;
        try { await navigator.clipboard.writeText(JSON.stringify(payload, null, 2)); setCopied(true); setTimeout(() => setCopied(false), 2000); }
        catch { toast.error("Kunne ikke kopiere"); }
    };

    return (
        <div data-testid="latest-sync-section" className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h3 className="text-base font-bold text-slate-900">Seneste integration-response</h3>
                <div className="flex items-center gap-2">
                    <StatusBadge status={status === "success" ? "active" : status === "warning" ? "error" : status === "error" ? "error" : "not_configured"} />
                    {isRealData ? (
                        <span className="inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-100">LIVE DATA</span>
                    ) : (
                        <span className="inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-100">FALLBACK DATA</span>
                    )}
                </div>
            </div>

            {!payload && !rawResponse && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-900">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                    <span>Der er ikke gemt nogen integration-response endnu.</span>
                </div>
            )}

            {payload && !data && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-900">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                    <span>Integration-response mangler data-objekt.</span>
                </div>
            )}

            {payload && data && !jobs && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-900">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                    <span>Integration-response mangler data.jobs.</span>
                </div>
            )}

            {missing.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 flex items-start gap-2 text-sm text-amber-900">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                    <span>ADVARSEL: Integration-response mangler arrays: <strong>{missing.join(", ")}</strong>.</span>
                </div>
            )}

            {payload && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-xs mb-3">
                    <div className="bg-slate-50 rounded-lg p-2"><p className="text-[10px] uppercase tracking-wider text-slate-400">Synced at</p><p className="font-mono text-slate-800 truncate">{latest?.latest_sync_at ? new Date(latest.latest_sync_at).toLocaleString("da-DK") : "—"}</p></div>
                    <div className="bg-slate-50 rounded-lg p-2"><p className="text-[10px] uppercase tracking-wider text-slate-400">Source</p><p className="font-mono text-slate-800 truncate">{payload?.source || "—"}</p></div>
                    <div className="bg-slate-50 rounded-lg p-2"><p className="text-[10px] uppercase tracking-wider text-slate-400">Sync type</p><p className="font-mono text-slate-800 truncate">{payload?.sync_type || payload?.type || "—"}</p></div>
                    <div className="bg-slate-50 rounded-lg p-2"><p className="text-[10px] uppercase tracking-wider text-slate-400">Company ID</p><p className="font-mono text-slate-800 truncate">{payload?.company_id || "—"}</p></div>
                    <div className="bg-slate-50 rounded-lg p-2"><p className="text-[10px] uppercase tracking-wider text-slate-400">Sheet ID</p><p className="font-mono text-slate-800 truncate">{payload?.sheet_id || "—"}</p></div>
                </div>
            )}

            {payload && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-xs mb-4">
                    {[
                        { k: "jobs_count", l: "Jobs" }, { k: "customers_count", l: "Kunder" },
                        { k: "employees_count", l: "Medarb." }, { k: "materials_count", l: "Materialer" },
                        { k: "purchase_requests_count", l: "Indkøb" },
                        { k: "invoice_ready_count", l: "Faktura" }, { k: "logs_count", l: "Logs" },
                    ].map((c) => (
                        <div key={c.k} className="bg-emerald-50 border border-emerald-100 rounded-lg p-2 text-center">
                            <p className="text-[10px] uppercase tracking-wider text-emerald-700">{c.l}</p>
                            <p className="text-lg font-bold text-emerald-700">{counts[c.k] ?? 0}</p>
                        </div>
                    ))}
                </div>
            )}

            {latest?.latest_sync_error && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-3 text-sm text-red-700">{latest.latest_sync_error}</div>
            )}

            {(payload || rawResponse) && (
                <div className="mb-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Preview raw integration-response</p>
                    <pre data-testid="latest-sync-json" className="text-[11px] bg-slate-900 text-slate-100 rounded-xl p-3 max-h-80 overflow-auto font-mono">{payload ? JSON.stringify(payload, null, 2) : rawResponse}</pre>
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                <Button onClick={copyJson} disabled={!payload} data-testid="latest-sync-copy" variant="outline">
                    {copied ? <><CheckCircle2 className="w-4 h-4 mr-1.5 text-emerald-600" />Kopieret</> : <><Copy className="w-4 h-4 mr-1.5" />Kopiér JSON</>}
                </Button>
                <Button onClick={() => downloadJson(payload, "myservicehelper-latest-sync-response.json")} disabled={!payload} variant="outline" data-testid="latest-sync-download">
                    <Download className="w-4 h-4 mr-1.5" />Download latest sync response
                </Button>
                <Button onClick={() => downloadJson(jobs || [], "myservicehelper-jobs-preview.json")} disabled={!jobs} variant="outline" data-testid="latest-sync-download-jobs">
                    <Download className="w-4 h-4 mr-1.5" />Download jobs
                </Button>
                <Button onClick={() => downloadJson(data?.customers || [], "myservicehelper-customers.json")} disabled={!data?.customers} variant="outline" data-testid="latest-sync-download-customers">
                    <Download className="w-4 h-4 mr-1.5" />Download customers
                </Button>
                <Button onClick={() => downloadJson(data?.employees || [], "myservicehelper-employees.json")} disabled={!data?.employees} variant="outline" data-testid="latest-sync-download-employees">
                    <Download className="w-4 h-4 mr-1.5" />Download employees
                </Button>
                <Button onClick={() => downloadJson(data?.materials || [], "myservicehelper-materials.json")} disabled={!data?.materials} variant="outline" data-testid="latest-sync-download-materials">
                    <Download className="w-4 h-4 mr-1.5" />Download materials
                </Button>
                <Button onClick={() => downloadJson(data?.invoice_ready || [], "myservicehelper-invoices.json")} disabled={!data?.invoice_ready} variant="outline" data-testid="latest-sync-download-invoices">
                    <Download className="w-4 h-4 mr-1.5" />Download invoices
                </Button>
                <Button onClick={() => downloadJson(data?.logs || [], "myservicehelper-logs.json")} disabled={!data?.logs} variant="outline" data-testid="latest-sync-download-logs">
                    <Download className="w-4 h-4 mr-1.5" />Download logs
                </Button>
                <Button onClick={reload} variant="outline" data-testid="latest-sync-reload">Genindlæs</Button>
            </div>
        </div>
    );
}
function LatestPostCallsSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIdx, setOpenIdx] = useState(0);

    const load = async () => {
        setLoading(true);
        try { setItems(await fetchPostCalls(50)); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const fmt = (iso) => iso ? new Date(iso).toLocaleString("da-DK") : "—";
    const active = items[openIdx] || null;

    let chatBubbles = [];
    if (active?.chat_messages_json) {
        try { const p = JSON.parse(active.chat_messages_json); if (Array.isArray(p)) chatBubbles = p; } catch (_) {}
    }

    return (
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="latest-post-calls-section">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-emerald-600" />
                        Seneste post-calls (ElevenLabs transcripts)
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">Modtaget via <code className="text-[11px]">POST /api/webhooks/post_call</code> · {items.length} samtaler i alt</p>
                </div>
                <Button onClick={load} variant="outline" size="sm" data-testid="post-calls-reload">
                    <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                    Genindlæs
                </Button>
            </div>

            {loading && items.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center">Indlæser…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-xl">
                    Ingen post-calls modtaget endnu. Send en via <code className="text-[11px]">POST /api/webhooks/post_call</code> for at se data her.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-[260px,1fr] gap-4">
                    {/* List */}
                    <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                        {items.map((pc, i) => (
                            <button
                                key={pc.post_call_id || pc.conversation_id || i}
                                type="button"
                                onClick={() => setOpenIdx(i)}
                                data-testid={`post-call-row-${i}`}
                                className={`w-full text-left px-3 py-2.5 hover:bg-emerald-50/40 transition-colors ${i === openIdx ? "bg-emerald-50/60" : ""}`}
                            >
                                <p className="text-sm font-semibold text-slate-900 truncate">{pc.customer_name || "Ukendt kunde"}</p>
                                <p className="text-[11px] text-slate-500 mt-0.5">{fmt(pc.call_started_at || pc.created_at)}</p>
                                {pc.call_duration_seconds ? (
                                    <p className="text-[10px] text-slate-400">{Math.round(pc.call_duration_seconds)} sek</p>
                                ) : null}
                            </button>
                        ))}
                    </div>

                    {/* Detail */}
                    <div className="space-y-3">
                        {active && (
                            <>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <Pair k="Kunde" v={active.customer_name} />
                                    <Pair k="Tid" v={fmt(active.call_started_at || active.created_at)} />
                                    <Pair k="Varighed" v={active.call_duration_seconds ? `${Math.round(active.call_duration_seconds)} sek` : "—"} />
                                    <Pair k="Post-call ID" v={active.post_call_id} mono />
                                </div>

                                {active.customer_original_message && (
                                    <div className="bg-slate-50 rounded-xl p-3 text-sm">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Kundens besked</div>
                                        <p className="text-slate-800 whitespace-pre-wrap">{active.customer_original_message}</p>
                                    </div>
                                )}
                                {active.ai_summary && (
                                    <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3 text-sm">
                                        <div className="text-[10px] uppercase tracking-wider text-blue-700 mb-1">AI-resume</div>
                                        <p className="text-slate-800 whitespace-pre-wrap">{active.ai_summary}</p>
                                    </div>
                                )}

                                {chatBubbles.length > 0 && (
                                    <div className="bg-slate-50 rounded-xl p-3 max-h-72 overflow-y-auto space-y-2 border border-slate-100">
                                        {chatBubbles.map((b, i) => {
                                            const isAgent = String(b.role || "").toLowerCase().match(/agent|assistant|ai|bot/);
                                            return (
                                                <div key={i} className={`flex flex-col ${isAgent ? "items-start" : "items-end"}`}>
                                                    <span className={`text-[10px] font-semibold mb-0.5 ${isAgent ? "text-blue-700" : "text-slate-600"}`}>
                                                        {isAgent ? "MyServiceHelper" : "Kunde"}
                                                    </span>
                                                    <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${isAgent ? "bg-white border border-slate-200 text-slate-800" : "bg-blue-600 text-white"}`}>
                                                        <p className="whitespace-pre-wrap">{b.text}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {active.transcript_display_text && chatBubbles.length === 0 && (
                                    <pre className="text-[11px] bg-slate-900 text-slate-100 rounded-xl p-3 max-h-60 overflow-auto font-mono whitespace-pre-wrap">{active.transcript_display_text}</pre>
                                )}

                                <div className="flex flex-wrap gap-2 pt-1">
                                    <Button onClick={() => downloadJson(active, `post-call_${active.customer_name || "ukendt"}_${(active.created_at || "").slice(0, 19).replace(/[:.]/g, "-")}.json`)} variant="outline" size="sm" data-testid="post-call-download-one">
                                        <Download className="w-3.5 h-3.5 mr-1.5" />Download denne
                                    </Button>
                                    <Button onClick={() => downloadJson(items, "myservicehelper-post-calls.json")} variant="outline" size="sm" data-testid="post-call-download-all">
                                        <Download className="w-3.5 h-3.5 mr-1.5" />Download alle ({items.length})
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function Pair({ k, v, mono }) {
    return (
        <div className="bg-slate-50/70 rounded-lg px-2.5 py-1.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{k}</p>
            <p className={`text-slate-900 ${mono ? "font-mono text-[11px]" : "text-sm"}`}>{v || "—"}</p>
        </div>
    );
}

// ---------- Tab 3: Sync & Logs ----------
function LogsTab({ logs, reload, canEdit, latestSync, reloadLatest }) {
    const [range, setRange] = useRange("week");
    const [source, setSource] = useState("");
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");

    const sources = ["n8n", "n8n_webhook", "voice_intake", "post_call", "elevenlabs", "google_sheets", "email", "intake", "dashboard"];
    const filtered = useMemo(() => {
        const ql = q.toLowerCase().trim();
        return logs.filter((l) => {
            if (source && l.source !== source) return false;
            if (status && l.status !== status) return false;
            if (!ql) return true;
            return [l.message, l.event_type, l.related_job_id].some((v) => (v || "").toString().toLowerCase().includes(ql));
        });
    }, [logs, source, status, q]);

    const counters = useMemo(() => {
        const lastBy = (s) => logs.find((l) => l.source === s);
        const errors = logs.filter((l) => l.status === "error").length;
        const jobsThisWeek = logs.filter((l) => l.event_type === "job_created").length;
        return {
            last_voice_intake: lastBy("voice_intake"),
            last_post_call: lastBy("post_call"),
            last_elevenlabs: lastBy("elevenlabs") || lastBy("intake"),
            last_email: lastBy("email"),
            errors,
            jobsThisWeek,
        };
    }, [logs]);

    const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("da-DK") : "—";

    const clearTests = async () => {
        if (!window.confirm("Slet alle test-logs?")) return;
        try { const r = await clearTestLogs(); toast.success(`Ryddet ${r.deleted} logs`); reload(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const downloadLogs = () => {
        downloadJson(filtered, `myservicehelper-integration-logs.json`);
    };

    return (
        <div className="space-y-4">
            <LatestSyncSection latest={latestSync} reload={reloadLatest} />
            <LatestPostCallsSection />

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Sidste Voice Intake</p>
                    <p className="text-sm font-semibold text-slate-900 mt-1">{counters.last_voice_intake?.event_type || "—"}</p>
                    <p className="text-xs text-slate-500">{fmtTime(counters.last_voice_intake?.created_at)}</p>
                </div>
                <button
                    type="button"
                    onClick={() => setSource("post_call")}
                    data-testid="logs-card-post-call"
                    className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 text-left hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors"
                >
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700">Sidste Post-Call</p>
                    <p className="text-sm font-semibold text-slate-900 mt-1">{counters.last_post_call?.event_type || "—"}</p>
                    <p className="text-xs text-slate-500">{fmtTime(counters.last_post_call?.created_at)}</p>
                </button>
                <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Sidste ElevenLabs</p>
                    <p className="text-sm font-semibold text-slate-900 mt-1">{counters.last_elevenlabs?.event_type || "—"}</p>
                    <p className="text-xs text-slate-500">{fmtTime(counters.last_elevenlabs?.created_at)}</p>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Sidste Email</p>
                    <p className="text-sm font-semibold text-slate-900 mt-1">{counters.last_email?.event_type || "—"}</p>
                    <p className="text-xs text-slate-500">{fmtTime(counters.last_email?.created_at)}</p>
                </div>
                <div className="bg-red-50 border border-red-100 rounded-2xl shadow-sm p-4">
                    <p className="text-[10px] uppercase tracking-wider text-red-700">Seneste fejl</p>
                    <p className="text-xl font-bold text-red-700 mt-1">{counters.errors}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl shadow-sm p-4">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700">Jobs auto-oprettet</p>
                    <p className="text-xl font-bold text-emerald-700 mt-1">{counters.jobsThisWeek}</p>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border-b border-slate-100">
                    <div className="flex flex-wrap items-center gap-2">
                        <Filter className="w-4 h-4 text-slate-400" />
                        <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="logs-source-filter" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                            <option value="">Alle kilder</option>
                            {sources.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="logs-status-filter" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                            <option value="">Alle status</option>
                            <option value="ok">OK</option>
                            <option value="error">Fejl</option>
                        </select>
                        <RangeToggle value={range} onChange={setRange} testid="logs-range" />
                    </div>
                    <div className="flex items-center gap-2">
                        <SearchInput value={q} onChange={setQ} placeholder="Søg log…" testid="logs-search" />
                        {canEdit && (
                            <Button onClick={clearTests} variant="outline" data-testid="logs-clear-tests" className="h-9 text-xs">Ryd test logs</Button>
                        )}
                        <Button onClick={downloadLogs} variant="outline" data-testid="logs-download" className="h-9 text-xs"><Download className="w-3.5 h-3.5 mr-1" />Download integration logs</Button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="px-4 py-3 text-left">Tid</th>
                                <th className="px-4 py-3 text-left">Kilde</th>
                                <th className="px-4 py-3 text-left">Event</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-left">Besked</th>
                                <th className="px-4 py-3 text-left">Job</th>
                                <th className="px-4 py-3 text-right">Fil</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((l) => (
                                <tr key={l.id} data-testid={`log-row-${l.id}`}>
                                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{fmtTime(l.created_at)}</td>
                                    <td className="px-4 py-2"><span className="inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{l.source}</span></td>
                                    <td className="px-4 py-2 text-slate-700">{l.event_type}</td>
                                    <td className="px-4 py-2"><StatusBadge status={l.status} /></td>
                                    <td className="px-4 py-2 text-slate-600 max-w-md truncate">{l.message || "—"}</td>
                                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{l.related_job_id || "—"}</td>
                                    <td className="px-4 py-2 text-right">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const ts = (l.created_at || "").replace(/[:.]/g, "-").slice(0, 19);
                                                const jobPart = l.related_job_id ? `_${l.related_job_id}` : "";
                                                downloadJson(l, `log_${l.source}_${l.event_type}${jobPart}_${ts}.json`);
                                            }}
                                            data-testid={`log-download-${l.id}`}
                                            title="Download denne log som JSON"
                                            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                        >
                                            <Download className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Ingen logs matcher.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ---------- Tab 4: Billing ----------
const ADDON_ICONS = { users: Users, wrench: Wrench, phone: Phone, command: Command };

function AddonRow({ catalogItem, draftQty, scheduledQty, onChange, currency = "kr" }) {
    const Icon = ADDON_ICONS[catalogItem.icon] || Users;
    const lineTotal = Number(draftQty || 0) * Number(catalogItem.unit_price || 0);
    const showScheduledNote = scheduledQty !== undefined && scheduledQty !== draftQty;
    return (
        <div
            data-testid={`addon-row-${catalogItem.id}`}
            className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0"
        >
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 inline-flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{catalogItem.name}</p>
                    <p className="text-xs text-slate-500">{Number(catalogItem.unit_price).toLocaleString("da-DK")} {currency}/md pr. {catalogItem.unit_label}</p>
                    {showScheduledNote && (
                        <p className="text-[11px] text-amber-600 mt-0.5">Træder i kraft næste periode: {scheduledQty}</p>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <button
                    data-testid={`addon-${catalogItem.id}-minus`}
                    type="button"
                    onClick={() => onChange(Math.max(0, Number(draftQty || 0) - 1))}
                    className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center justify-center transition disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={Number(draftQty || 0) <= 0}
                >
                    <Minus className="w-3.5 h-3.5" />
                </button>
                <input
                    data-testid={`addon-${catalogItem.id}-qty`}
                    type="number"
                    min="0"
                    value={draftQty}
                    onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || "0", 10)))}
                    className="w-14 h-8 text-center text-sm font-semibold border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                    data-testid={`addon-${catalogItem.id}-plus`}
                    type="button"
                    onClick={() => onChange(Number(draftQty || 0) + 1)}
                    className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center justify-center transition"
                >
                    <Plus className="w-3.5 h-3.5" />
                </button>
            </div>
            <div className="w-28 text-right shrink-0">
                <p className="text-sm font-semibold text-slate-900">{lineTotal.toLocaleString("da-DK")} {currency}</p>
                <p className="text-[11px] text-slate-400">/md</p>
            </div>
        </div>
    );
}

function BillingTab() {
    const [sub, setSub] = useState(null);
    const [packages, setPackages] = useState([]);
    const [addons, setAddons] = useState(null); // { catalog, current, scheduled, current_total_monthly }
    const [draftAddons, setDraftAddons] = useState(null);
    const [loading, setLoading] = useState(true);
    const [portalLoading, setPortalLoading] = useState(false);
    const [changingTo, setChangingTo] = useState(null);
    const [changeLoading, setChangeLoading] = useState(false);
    const [addonsSaving, setAddonsSaving] = useState(false);
    const [addonsConfirm, setAddonsConfirm] = useState(false);
    const [topupLoading, setTopupLoading] = useState("");

    const refresh = useCallback(async () => {
        try {
            const [s, p, a] = await Promise.all([
                fetchBillingSubscription(),
                fetchBillingPackages(),
                fetchBillingAddons(),
            ]);
            setSub(s);
            setPackages(p?.packages || []);
            setAddons(a);
            setDraftAddons(a?.current || {});
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const openPortal = async () => {
        setPortalLoading(true);
        try {
            const returnUrl = `${window.location.origin}/dashboard/settings?tab=billing`;
            const data = await createBillingPortalSession(returnUrl);
            if (data?.url) { window.location.href = data.url; return; }
            toast.error("Kunne ikke åbne kundeportal.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPortalLoading(false);
        }
    };

    const confirmChangePlan = async () => {
        if (!changingTo) return;
        setChangeLoading(true);
        try {
            const data = await changeBillingPlan(changingTo.id);
            if (data?.url) { window.location.href = data.url; return; }
            toast.error("Kunne ikke oprette betaling.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setChangeLoading(false);
            setChangingTo(null);
        }
    };

    const formatKr = (amount) => `${Number(amount || 0).toLocaleString("da-DK")} kr`;
    const formatDate = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleDateString("da-DK", { day: "2-digit", month: "long", year: "numeric" }); }
        catch { return iso; }
    };

    // Addon diff helpers (recurring monthly add-ons)
    const monthlyCatalog = addons?.monthly_catalog || [];
    const addonDiff = (() => {
        if (!addons || !draftAddons) return { hasChange: false, additions: [], reductions: [], addedCost: 0, newMonthlyTotal: 0 };
        const additions = [];
        const reductions = [];
        let addedCost = 0;
        monthlyCatalog.forEach((it) => {
            const cur = Number(addons.current[it.id] || 0);
            const next = Number(draftAddons[it.id] || 0);
            if (next > cur) {
                additions.push({ ...it, qty: next - cur });
                addedCost += (next - cur) * it.unit_price;
            } else if (next < cur) {
                reductions.push({ ...it, qty: cur - next });
            }
        });
        const newMonthlyTotal = monthlyCatalog.reduce((s, it) => s + Number(draftAddons[it.id] || 0) * it.unit_price, 0);
        const currentMonthlyTotal = monthlyCatalog.reduce((s, it) => s + Number(addons.current[it.id] || 0) * it.unit_price, 0);
        return { hasChange: additions.length > 0 || reductions.length > 0, additions, reductions, addedCost, newMonthlyTotal, currentMonthlyTotal };
    })();

    const setAddonQty = (id, qty) => {
        setDraftAddons((prev) => ({ ...prev, [id]: qty }));
    };

    const resetAddons = () => {
        setDraftAddons(addons?.current || {});
    };

    const submitAddons = async () => {
        if (!addonDiff.hasChange) return;
        setAddonsSaving(true);
        try {
            await updateBillingAddons(draftAddons);
            toast.success("Tilvalg opdateret.");
            await refresh();
            setAddonsConfirm(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAddonsSaving(false);
        }
    };

    const buyTopup = async (topupId) => {
        setTopupLoading(topupId);
        try {
            const data = await createTopupCheckout(topupId);
            if (data?.url) { window.location.href = data.url; return; }
            toast.error("Kunne ikke oprette betaling.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setTopupLoading("");
        }
    };

    const statusInfo = (() => {
        const s = sub?.subscription_status;
        if (s === "active") return { label: "Aktiv", color: "bg-emerald-50 text-emerald-700 border-emerald-100" };
        if (s === "trialing") return { label: "Prøveperiode", color: "bg-blue-50 text-blue-700 border-blue-100" };
        if (s === "past_due") return { label: "Forfalden", color: "bg-amber-50 text-amber-700 border-amber-100" };
        if (s === "canceled") return { label: "Annulleret", color: "bg-red-50 text-red-700 border-red-100" };
        if (s === "grandfathered") return { label: "Grandfathered (gratis adgang)", color: "bg-slate-50 text-slate-700 border-slate-200" };
        return { label: s || "Intet abonnement", color: "bg-slate-100 text-slate-600 border-slate-200" };
    })();

    const currentPlanId = sub?.plan || "";
    const planLabel = (() => {
        if (!currentPlanId || currentPlanId === "none") return "Intet abonnement";
        const m = packages.find((p) => p.id === currentPlanId);
        return m ? m.name : currentPlanId.charAt(0).toUpperCase() + currentPlanId.slice(1);
    })();

    const hasStripeCustomer = Boolean(sub?.stripe_customer_id);
    const isActive = sub?.subscription_status === "active" || sub?.subscription_status === "trialing";
    const otherPackages = packages.filter((p) => p.id !== currentPlanId);
    const showChangePlan = isActive && otherPackages.length > 0;
    const currentPkgMeta = packages.find((p) => p.id === currentPlanId);
    const canEditAddons = isActive || sub?.subscription_status === "grandfathered";

    if (loading) {
        return <div data-testid="billing-tab-loading" className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Henter abonnement…</div>;
    }

    return (
        <div data-testid="billing-tab" className="space-y-6 max-w-4xl">
            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-6">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-blue-600" />
                            <h3 className="text-lg font-semibold text-slate-900">Dit abonnement</h3>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">Administrér din betaling, opdatér kort eller annullér via Stripes sikre kundeportal.</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusInfo.color}`} data-testid="billing-status-badge">
                        {statusInfo.label}
                    </span>
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Plan" value={planLabel} testid="billing-plan" />
                    <Field label="Næste betaling" value={formatDate(sub?.current_period_end)} testid="billing-next-billing" />
                    <Field label="Status" value={statusInfo.label} testid="billing-status-value" />
                </div>

                {/* Månedlige minutter — limit + top-up saldo */}
                {addons && (
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="minutes-summary">
                        <Field label="Månedlige AI-minutter" value={`${addons.monthly_limit_minutes ?? 300} min./md.`} testid="billing-monthly-limit" />
                        <Field label="Ekstra saldo (engangskøb)" value={`${addons.topup_minutes_remaining ?? 0} min.`} testid="billing-topup-remaining" />
                    </div>
                )}

                {/* Ekstra tilvalg — recurring monthly add-ons */}
                {addons && (
                    <div className="mt-6 pt-5 border-t border-slate-100" data-testid="addons-section">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <h4 className="text-sm font-semibold text-slate-900">Månedlige tilvalg</h4>
                            {!canEditAddons && (
                                <span className="text-xs text-slate-400">Aktivt abonnement kræves for at justere</span>
                            )}
                        </div>
                        <div className={canEditAddons ? "" : "opacity-50 pointer-events-none"}>
                            {monthlyCatalog.map((it) => (
                                <AddonRow
                                    key={it.id}
                                    catalogItem={it}
                                    draftQty={draftAddons?.[it.id] ?? 0}
                                    onChange={(q) => setAddonQty(it.id, q)}
                                />
                            ))}
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-1">
                            <div className="text-sm text-slate-600">
                                {addonDiff.hasChange ? (
                                    <>
                                        Nuværende: <strong>{formatKr(addonDiff.currentMonthlyTotal)}</strong>/md →
                                        <span className="ml-2">Ny: <strong className="text-blue-600">{formatKr(addonDiff.newMonthlyTotal)}</strong>/md</span>
                                    </>
                                ) : (
                                    <>Total tilvalg: <strong>{formatKr(addonDiff.currentMonthlyTotal)}</strong>/md</>
                                )}
                            </div>
                            {addonDiff.hasChange && (
                                <div className="flex gap-2">
                                    <Button data-testid="addons-reset-btn" variant="outline" size="sm" onClick={resetAddons} disabled={addonsSaving}>Annullér ændringer</Button>
                                    <Button
                                        data-testid="addons-save-btn"
                                        onClick={() => setAddonsConfirm(true)}
                                        disabled={addonsSaving}
                                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                                    >
                                        {addonsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                        Gem ændringer
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Engangskøb — one-time top-ups */}
                        <div className="mt-5 pt-5 border-t border-slate-100" data-testid="topup-section">
                            <h4 className="text-sm font-semibold text-slate-900 mb-1">Køb ekstra minutter (engangskøb)</h4>
                            <p className="text-xs text-slate-500 mb-3">Lægges oven i din saldo og resetter ikke hver måned.</p>
                            <div className={`flex flex-wrap gap-3 ${canEditAddons ? "" : "opacity-50 pointer-events-none"}`}>
                                {(addons.topup_catalog || []).map((t) => (
                                    <Button
                                        key={t.id}
                                        data-testid={`topup-buy-${t.id}`}
                                        variant="outline"
                                        onClick={() => buyTopup(t.id)}
                                        disabled={!!topupLoading}
                                        className="h-11 px-4 rounded-xl border-slate-300"
                                    >
                                        {topupLoading === t.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                                        {t.name} · {formatKr(t.price)}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-6 pt-5 border-t border-slate-100 flex flex-wrap gap-3 items-center">
                    {hasStripeCustomer ? (
                        <Button
                            data-testid="billing-manage-btn"
                            onClick={openPortal}
                            disabled={portalLoading}
                            className="h-11 px-6 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                        >
                            {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                            Administrér abonnement
                        </Button>
                    ) : (
                        <Button
                            data-testid="billing-start-subscription-btn"
                            onClick={() => { window.location.href = "/priser"; }}
                            className="h-11 px-6 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                        >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Vælg abonnement
                        </Button>
                    )}
                    {!hasStripeCustomer && (
                        <p className="text-xs text-slate-400">Når du har gennemført dit første køb, kan du administrere abonnementet her.</p>
                    )}
                </div>
            </div>

            {/* Skift plan */}
            {showChangePlan && (
                <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-6" data-testid="change-plan-section">
                    <h3 className="text-lg font-semibold text-slate-900">Skift plan</h3>
                    <p className="text-sm text-slate-500 mt-1">Opgrader eller skift din plan med ét klik. Din nye plan starter med det samme efter betaling.</p>

                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {currentPkgMeta && (
                            <div data-testid={`plan-card-current-${currentPkgMeta.id}`} className="rounded-xl border-2 border-blue-500 bg-blue-50/30 p-5 relative">
                                <span className="absolute -top-2.5 left-4 text-[10px] uppercase tracking-wider bg-blue-600 text-white font-bold px-2 py-0.5 rounded-full">Nuværende plan</span>
                                <h4 className="text-base font-bold text-slate-900">{currentPkgMeta.name}</h4>
                                <p className="text-2xl font-extrabold text-slate-900 mt-1">{formatKr(currentPkgMeta.amount)}<span className="text-sm text-slate-500 font-normal">/md</span></p>
                                <p className="text-xs text-slate-500 mt-2">Du er her</p>
                            </div>
                        )}
                        {otherPackages.map((pkg) => (
                            <div key={pkg.id} data-testid={`plan-card-${pkg.id}`} className="rounded-xl border border-slate-200 bg-white p-5 hover:border-blue-300 hover:shadow-md transition">
                                <h4 className="text-base font-bold text-slate-900">{pkg.name}</h4>
                                <p className="text-2xl font-extrabold text-slate-900 mt-1">{formatKr(pkg.amount)}<span className="text-sm text-slate-500 font-normal">/md</span></p>
                                <Button
                                    data-testid={`change-to-${pkg.id}-btn`}
                                    onClick={() => setChangingTo(pkg)}
                                    className="mt-4 w-full rounded-full h-10 bg-slate-900 hover:bg-slate-800 text-white"
                                >
                                    Skift til {pkg.name}
                                </Button>
                            </div>
                        ))}
                        <div data-testid="plan-card-enterprise" className="rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-5 hover:border-slate-400 transition">
                            <h4 className="text-base font-bold text-slate-900">Enterprise</h4>
                            <p className="text-sm text-slate-600 mt-1">Skræddersyet pris og setup til større teams.</p>
                            <Button
                                data-testid="change-to-enterprise-btn"
                                onClick={() => { window.location.href = "/kontakt"; }}
                                variant="outline"
                                className="mt-4 w-full rounded-full h-10 border-slate-300"
                            >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Kontakt salg
                            </Button>
                        </div>
                    </div>

                    <p className="mt-4 text-xs text-slate-400">
                        Din nye plan starter ved betaling. Eventuelt resterende beløb fra nuværende periode er ikke refunderet.
                    </p>
                </div>
            )}

            <Dialog open={!!changingTo} onOpenChange={(o) => !o && setChangingTo(null)}>
                <DialogContent className="sm:max-w-md" data-testid="change-plan-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>Bekræft planskifte</DialogTitle>
                    </DialogHeader>
                    {changingTo && (
                        <div className="space-y-4 text-sm">
                            <p className="text-slate-700">
                                Du skifter fra <strong>{currentPkgMeta?.name || planLabel}</strong>
                                {currentPkgMeta && <> ({formatKr(currentPkgMeta.amount)}/md)</>} til <strong>{changingTo.name}</strong> ({formatKr(changingTo.amount)}/md).
                            </p>
                            <p className="text-slate-500">
                                Din nye plan starter med det samme efter betaling. Eventuelt resterende beløb fra din nuværende periode er ikke refunderet.
                            </p>
                            <div className="flex gap-2 justify-end pt-2">
                                <Button data-testid="change-plan-cancel-btn" variant="outline" onClick={() => setChangingTo(null)} disabled={changeLoading}>Annullér</Button>
                                <Button
                                    data-testid="change-plan-confirm-btn"
                                    onClick={confirmChangePlan}
                                    disabled={changeLoading}
                                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                                >
                                    {changeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                                    Fortsæt til betaling
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Addons confirmation modal */}
            <Dialog open={addonsConfirm} onOpenChange={(o) => !o && setAddonsConfirm(false)}>
                <DialogContent className="sm:max-w-md" data-testid="addons-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>Bekræft ændringer</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        {addonDiff.additions.length > 0 && (
                            <div>
                                <p className="font-semibold text-emerald-700 mb-1">Du tilføjer:</p>
                                <ul className="text-slate-700 space-y-1 pl-1">
                                    {addonDiff.additions.map((a) => (
                                        <li key={a.id} data-testid={`addons-confirm-add-${a.id}`}>+{a.qty} {a.name} <span className="text-slate-400">({formatKr(a.qty * a.unit_price)}/md)</span></li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {addonDiff.reductions.length > 0 && (
                            <div>
                                <p className="font-semibold text-amber-700 mb-1">Du fjerner:</p>
                                <ul className="text-slate-700 space-y-1 pl-1">
                                    {addonDiff.reductions.map((r) => (
                                        <li key={r.id} data-testid={`addons-confirm-remove-${r.id}`}>−{r.qty} {r.name} <span className="text-slate-400">(spares {formatKr(r.qty * r.unit_price)}/md)</span></li>
                                    ))}
                                </ul>
                                <p className="text-xs text-amber-600 mt-1">Ændringen opdateres med det samme (proration via Stripe).</p>
                            </div>
                        )}
                        {addonDiff.addedCost > 0 && (
                            <div className="bg-slate-50 rounded-lg p-3">
                                <p className="text-slate-700">Tillæg pr. måned:</p>
                                <p className="text-xl font-bold text-blue-600 mt-1">{formatKr(addonDiff.addedCost)}/md</p>
                                <p className="text-xs text-slate-500 mt-1">Et forholdsmæssigt beløb trækkes nu via Stripe for resten af perioden.</p>
                            </div>
                        )}
                        <div className="flex gap-2 justify-end pt-2">
                            <Button data-testid="addons-confirm-cancel-btn" variant="outline" onClick={() => setAddonsConfirm(false)} disabled={addonsSaving}>Annullér</Button>
                            <Button
                                data-testid="addons-confirm-submit-btn"
                                onClick={submitAddons}
                                disabled={addonsSaving}
                                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                            >
                                {addonsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                                Bekræft
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <div className="bg-slate-50 border border-slate-200/70 rounded-2xl p-5 text-sm text-slate-600">
                <p className="font-semibold text-slate-800 mb-1">Hvad kan jeg gøre i kundeportalen?</p>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                    <li>Opdatér betalingskort</li>
                    <li>Se og hent tidligere fakturaer</li>
                    <li>Skift plan eller faktureringsinterval</li>
                    <li>Annullér abonnementet</li>
                </ul>
            </div>
        </div>
    );
}

function Field({ label, value, testid }) {
    return (
        <div className="bg-slate-50 rounded-xl p-3" data-testid={testid}>
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
            <p className="text-slate-900 font-medium mt-0.5">{value}</p>
        </div>
    );
}

// ---------- Tab 5: Sikkerhed (placeholder) ----------
function SecurityTab() {
    return (
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-6 flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
                <h3 className="text-base font-bold text-slate-900">Sikkerhed</h3>
                <p className="text-sm text-slate-600 mt-1">Adgangskontrol, 2FA og audit-logs kommer i V2. Lige nu:</p>
                <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc pl-5">
                    <li>API keys og tokens vises kun som ••••••••• efter de er gemt.</li>
                    <li>Kun owner/admin må ændre integrationer.</li>
                    <li>Webhook-payloads gemmes med raw_payload til debugging.</li>
                    <li>Alle integrationer er scoped på company_id.</li>
                </ul>
            </div>
        </div>
    );
}

// ---------- Tab 5: Firma (billing profile for invoices) ----------
function CompanyField({ k, label, type = "text", placeholder, hint, full = false, value, onChange, ...rest }) {
    return (
        <div className={`space-y-1 ${full ? "sm:col-span-2" : ""}`}>
            <Label className="text-xs font-medium text-slate-700">{label}</Label>
            <Input
                value={value ?? ""}
                onChange={(e) => onChange(k, e.target.value)}
                type={type}
                placeholder={placeholder}
                data-testid={`company-${k}`}
                className="h-10 bg-white border-slate-200"
                {...rest}
            />
            {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
        </div>
    );
}

function CompanyGroup({ title, icon: Icon, children }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 mb-4">
            <div className="flex items-center gap-2 mb-4">
                {Icon && <Icon className="w-4 h-4 text-blue-600" />}
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">{title}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
        </div>
    );
}

function CompanyTab({ user }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const p = await fetchBillingProfile();
                setProfile(p);
            } catch (e) {
                setError(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const setField = (k, v) => setProfile((p) => ({ ...(p || {}), [k]: v }));

    const handleSave = async () => {
        setSaving(true);
        setError("");
        try {
            const payload = { ...profile };
            delete payload.name;
            delete payload.invoice_next_number;
            delete payload.billing_profile_updated_at;
            const res = await updateBillingProfile(payload);
            toast.success("Firmaoplysninger gemt");
            setProfile((p) => ({ ...p, ...(res.updated || {}) }));
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-6 flex items-center justify-center" data-testid="company-tab-loading">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div data-testid="company-tab">
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-sm text-red-700 inline-flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
                </div>
            )}

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4 text-xs text-blue-900 flex items-start gap-2">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <p>Disse oplysninger bruges på alle fakturaer du udsteder. CVR og adresse er <strong>lovpligtige</strong> på danske fakturaer.</p>
            </div>

            <CompanyGroup title="Virksomhed" icon={Building2}>
                <div className="sm:col-span-2 bg-slate-50 rounded-xl p-3 -mt-1">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Brand-navn (vises som afsender)</p>
                    <p className="font-medium text-slate-900">{profile?.name || user?.name || "—"}</p>
                </div>
                <CompanyField k="legal_name" label="Registreret virksomhedsnavn *" placeholder="fx Gregersen Service ApS" value={profile?.legal_name} onChange={setField} full />
                <CompanyField k="cvr" label="CVR *" placeholder="12345678" hint="8 cifre (DK)" value={profile?.cvr} onChange={setField} />
                <CompanyField k="vat_number" label="Moms-nr. (VAT)" placeholder="DK12345678" hint="Udfyldes automatisk fra CVR" value={profile?.vat_number} onChange={setField} />
            </CompanyGroup>

            <CompanyGroup title="Adresse" icon={Building2}>
                <CompanyField k="address" label="Adresse *" placeholder="Hovedgaden 12" value={profile?.address} onChange={setField} full />
                <CompanyField k="postal_code" label="Postnr." placeholder="8000" value={profile?.postal_code} onChange={setField} />
                <CompanyField k="city" label="By" placeholder="Aarhus C" value={profile?.city} onChange={setField} />
                <CompanyField k="country" label="Land" placeholder="Danmark" value={profile?.country} onChange={setField} />
            </CompanyGroup>

            <CompanyGroup title="Kontakt (vises på faktura)" icon={Mail}>
                <CompanyField k="billing_email" label="Faktura-email" type="email" placeholder="faktura@dit-firma.dk" value={profile?.billing_email} onChange={setField} />
                <CompanyField k="billing_phone" label="Telefon" placeholder="70123456" value={profile?.billing_phone} onChange={setField} />
                <CompanyField k="website" label="Hjemmeside" placeholder="https://dit-firma.dk" value={profile?.website} onChange={setField} full />
            </CompanyGroup>

            <CompanyGroup title="Bankoplysninger (for betaling)" icon={CreditCard}>
                <CompanyField k="bank_name" label="Bank" placeholder="Sydbank" value={profile?.bank_name} onChange={setField} />
                <CompanyField k="bank_account_reg" label="Reg.nr." placeholder="7000" value={profile?.bank_account_reg} onChange={setField} />
                <CompanyField k="bank_account_number" label="Kontonr." placeholder="0001234567" value={profile?.bank_account_number} onChange={setField} />
                <CompanyField k="iban" label="IBAN" placeholder="DK1430000001234567" value={profile?.iban} onChange={setField} />
                <CompanyField k="swift_bic" label="SWIFT / BIC" placeholder="SYBKDK22" value={profile?.swift_bic} onChange={setField} />
                <CompanyField k="mobilepay" label="MobilePay nr." placeholder="12345" value={profile?.mobilepay} onChange={setField} />
            </CompanyGroup>

            <CompanyGroup title="Standard-priser (auto-faktura)" icon={Info}>
                <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-700">Timepris (kr/time)</Label>
                    <Input
                        value={profile?.default_hourly_rate ?? 650}
                        onChange={(e) => setField("default_hourly_rate", parseFloat(e.target.value) || 0)}
                        type="number"
                        step="any"
                        min={0}
                        max={100000}
                        data-testid="company-default_hourly_rate"
                        className="h-10 bg-white border-slate-200"
                    />
                    <p className="text-[11px] text-slate-400">Bruges når medarbejderens egen timepris ikke er sat. Fallback: 650 kr.</p>
                </div>
                <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-700">Km-pris (kr/km)</Label>
                    <Input
                        value={profile?.default_km_rate ?? 3.79}
                        onChange={(e) => setField("default_km_rate", parseFloat(e.target.value) || 0)}
                        type="number"
                        step="0.01"
                        min={0}
                        max={1000}
                        data-testid="company-default_km_rate"
                        className="h-10 bg-white border-slate-200"
                    />
                    <p className="text-[11px] text-slate-400">Bruges på kørsels-linjen i auto-faktura. Fallback: 3,79 kr.</p>
                </div>
            </CompanyGroup>

            <CompanyGroup title="Fakturastandard" icon={Info}>
                <CompanyField k="invoice_prefix" label="Fakturanr.-præfiks" placeholder="MSH-" hint="fx 'MSH-' → MSH-2026-0001" value={profile?.invoice_prefix} onChange={setField} />
                <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-700">Næste fakturanummer</Label>
                    <Input
                        value={profile?.invoice_next_number || 1}
                        type="number"
                        readOnly
                        disabled
                        className="h-10 bg-slate-50 border-slate-200 text-slate-500"
                        data-testid="company-invoice_next_number"
                    />
                    <p className="text-[11px] text-slate-400">Tæller automatisk op pr. udstedt faktura</p>
                </div>
                <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-700">Standard betalingsfrist (dage)</Label>
                    <Input
                        value={profile?.default_payment_terms_days ?? 8}
                        onChange={(e) => setField("default_payment_terms_days", parseInt(e.target.value, 10) || 0)}
                        type="number"
                        min={0}
                        max={180}
                        data-testid="company-default_payment_terms_days"
                        className="h-10 bg-white border-slate-200"
                    />
                </div>
                <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs font-medium text-slate-700">Standard betalingsinfo (vises nederst på faktura)</Label>
                    <textarea
                        value={profile?.default_payment_info ?? ""}
                        onChange={(e) => setField("default_payment_info", e.target.value)}
                        placeholder="Betal til reg. 7000 konto 0001234567 med fakturanr. som reference. Eller via MobilePay til 12345."
                        rows={3}
                        data-testid="company-default_payment_info"
                        className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
                    />
                </div>
                <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs font-medium text-slate-700">Standard note (valgfri)</Label>
                    <textarea
                        value={profile?.default_invoice_note ?? ""}
                        onChange={(e) => setField("default_invoice_note", e.target.value)}
                        placeholder="fx 'Tak for handlen!' eller garanti-info"
                        rows={2}
                        data-testid="company-default_invoice_note"
                        className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
                    />
                </div>
            </CompanyGroup>

            <div className="flex items-center justify-end gap-3">
                <p className="text-xs text-slate-400 mr-auto">* Lovpligtige felter for danske fakturaer</p>
                <Button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="company-save"
                    className="h-11 px-6 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white font-medium"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    {saving ? "Gemmer…" : "Gem firmaoplysninger"}
                </Button>
            </div>
        </div>
    );
}

// ---------- Main page ----------
export default function Settings() {
    const { user } = useAuth();
    const initialTab = (() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const t = params.get("tab");
            const known = ["integrations", "ai-data", "mapping", "logs", "billing", "security", "company"];
            return known.includes(t) ? t : "integrations";
        } catch { return "integrations"; }
    })();
    const [tab, setTab] = useState(initialTab);
    const [conns, setConns] = useState({});
    const [logs, setLogs] = useState([]);
    const [latestSync, setLatestSync] = useState(null);
    const canEdit = user && (user.role === "owner" || user.role === "admin");

    const loadConns = async () => {
        try {
            const list = await fetchConnections();
            const map = {};
            list.forEach((c) => { map[c.type] = c; });
            setConns(map);
        } catch {}
    };

    const loadLogs = async () => {
        try { setLogs(await fetchIntegrationLogs({ limit: 200 })); } catch {}
    };

    const loadLatestSync = async () => {
        try { setLatestSync(await fetchLatestSync("n8n")); } catch {}
    };

    useEffect(() => { loadConns(); loadLogs(); loadLatestSync(); }, []);

    const onConnSaved = (saved, type) => {
        setConns((p) => ({ ...p, [type || saved.type]: saved }));
        // After test/sync, the latest_sync_payload may have changed
        loadLatestSync();
    };

    return (
        <div data-testid="settings-page" className="p-5 sm:p-8 max-w-7xl mx-auto">
            <PageHeader title="Settings" subtitle="Integrationer, data mapping og logs — kontrolrum for MyServiceHelper." />

            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-1.5 inline-flex flex-wrap gap-1 mb-6">
                {TABS.map((t) => (
                    <button
                        key={t.v}
                        data-testid={`settings-tab-${t.v}`}
                        onClick={() => setTab(t.v)}
                        className={`px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${tab === t.v ? "bg-blue-600 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}
                    >{t.l}</button>
                ))}
            </div>

            {tab === "integrations" && <AIPhonePanel />}
            {tab === "ai-data" && <AICompanyDataTab canEdit={canEdit} />}
            {tab === "mapping" && <MappingTab canEdit={canEdit} />}
            {tab === "logs" && <LogsTab logs={logs} reload={loadLogs} canEdit={canEdit} latestSync={latestSync} reloadLatest={loadLatestSync} />}
            {tab === "billing" && <BillingTab />}
            {tab === "security" && <SecurityTab />}
            {tab === "company" && <CompanyTab user={user} />}

            {!canEdit && (
                <p className="text-xs text-slate-400 mt-6">Kun owner/admin kan ændre integrationer og mapping.</p>
            )}
        </div>
    );
}
